import React, { useState } from 'react';

/**
 * HonestFailure — B1 of Tier 1 (2026-04-29).
 *
 * Renders the structured failure_explanation returned by the backend when the
 * SQL pipeline can't produce a confident answer. Replaces the previous
 * behaviour where a hardcoded fallback query (often ignoring user filters)
 * was returned silently — see F26 in the brainstorming session.
 *
 * The user sees:
 *   - Plain summary: "I couldn't generate a confident answer for this question."
 *   - Rephrasing hints: actionable, derived from validation error patterns
 *   - Collapsible details: tables considered, validation errors, attempted SQL
 *
 * Goal: honest "I don't know" beats confident-wrong.
 *
 * Props
 * -----
 * failureExplanation  Object  shape: {
 *   tried_sql: string,
 *   tables_considered: string[],
 *   validation_errors: string[],
 *   validation_warnings: string[],
 *   validation_feedback: string,
 *   branch_attempted: string,
 *   schema_gate_retries: number,
 *   rephrasing_hints: string[]
 * }
 */
function HonestFailure({ failureExplanation }) {
  const [showDetails, setShowDetails] = useState(false);

  if (!failureExplanation) return null;

  const {
    tried_sql,
    tables_considered = [],
    validation_errors = [],
    validation_feedback = '',
    branch_attempted,
    schema_gate_retries,
    rephrasing_hints = [],
  } = failureExplanation;

  const containerStyle = {
    marginTop: '0.5rem',
    padding: '0.85rem 1rem',
    borderLeft: '3px solid #f59e0b',  // amber-500
    background: '#fffbeb',             // amber-50
    borderRadius: '0.4rem',
    fontSize: '0.85rem',
    color: '#374151',
  };

  const sectionLabelStyle = {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginTop: '0.5rem',
  };

  const detailsToggleStyle = {
    fontSize: '0.75rem',
    color: '#6366f1',     // indigo-500
    background: 'none',
    border: 'none',
    padding: '0.4rem 0',
    cursor: 'pointer',
    textDecoration: 'underline',
  };

  const codeBlockStyle = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.72rem',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '0.25rem',
    padding: '0.5rem',
    marginTop: '0.3rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#374151',
  };

  return (
    <div style={containerStyle}>
      {rephrasing_hints.length > 0 && (
        <>
          <div style={sectionLabelStyle}>How to rephrase</div>
          <ul style={{ margin: '0.3rem 0 0 1.2rem', padding: 0 }}>
            {rephrasing_hints.map((hint, i) => (
              <li key={i} style={{ marginBottom: '0.25rem' }}>{hint}</li>
            ))}
          </ul>
        </>
      )}

      <button
        type="button"
        style={detailsToggleStyle}
        onClick={() => setShowDetails(!showDetails)}
      >
        {showDetails ? '▴ Hide details' : '▾ Show what I tried'}
      </button>

      {showDetails && (
        <div>
          {tables_considered.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Tables I considered</div>
              <div style={{ marginTop: '0.2rem' }}>
                {tables_considered.map((t, i) => (
                  <code
                    key={i}
                    style={{
                      display: 'inline-block',
                      background: '#f3f4f6',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.25rem',
                      padding: '0.1rem 0.4rem',
                      marginRight: '0.3rem',
                      marginBottom: '0.2rem',
                      fontSize: '0.72rem',
                    }}
                  >
                    {t}
                  </code>
                ))}
              </div>
            </>
          )}

          {validation_errors.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Why it didn't validate</div>
              <ul style={{ margin: '0.3rem 0 0 1.2rem', padding: 0 }}>
                {validation_errors.map((err, i) => (
                  <li key={i} style={{ fontSize: '0.78rem', marginBottom: '0.2rem' }}>
                    {err}
                  </li>
                ))}
              </ul>
              {validation_feedback && (
                <div style={{ fontSize: '0.78rem', marginTop: '0.3rem', color: '#6b7280' }}>
                  {validation_feedback}
                </div>
              )}
            </>
          )}

          {tried_sql && (
            <>
              <div style={sectionLabelStyle}>SQL I tried (failed validation)</div>
              <pre style={codeBlockStyle}>{tried_sql}</pre>
            </>
          )}

          <div style={{
            ...sectionLabelStyle,
            marginTop: '0.6rem',
            color: '#9ca3af',
          }}>
            Pipeline diagnostics
          </div>
          <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
            Branch: <code>{branch_attempted || 'unknown'}</code>
            {typeof schema_gate_retries === 'number' && (
              <> · Schema-gate retries: {schema_gate_retries}</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HonestFailure;
